<template>
  <div class="subscription-selector">
    <div
      v-if="plan"
      class="plan-summary"
      :class="{ enabled: !noChoice }"
      @click.prevent="planSelectorClicked"
    >
      <span class="plan-name"
        >{{ plan.name }}
        <span v-if="plan.min_users > 1" class="min-users"
          >({{ $t("message.min-x-users", [plan.min_users]) }})</span
        ></span
      >
      <span class="plan-description">
        <span v-if="pricingPeriod === 'monthly'">
          {{ learners.length }} {{ $t("message.learners-at") }}
          <span>
            ${{ unitPrice }}
          </span>
          {{ $t("message.each-per-month") }}
        </span>
        <span v-else>
          {{ licenceCount }} {{ $t("message.licenses-at") }}
          <span>
            ${{ unitPrice }}
          </span>
          {{ $t("message.each-per-year") }}
        </span>
        <span
          class="proviso"
          v-if="proviso"
          v-html="proviso"
        ></span>
      </span>
      <div class="inline-features" v-if="noChoice">
        <p v-for="feature in plan.features">{{ feature }}</p>
      </div>
    </div>
    <div class="annual-nudge" v-if="displayAnnualNudge">
      <div class="description-nudge-section">
        <span
          v-if="plan.annual_switch_message"
          v-html="plan.annual_switch_message"
        ></span>
        <span v-else>
          {{ $t("message.like-to-save") }}
          <span class="reduction-text-bold"
            >${{ annualReduction }} {{ $t("message.per-learner") }}</span
          >
          {{ $t("message.switching-to-annual") }}?
          <span v-if="discountActive"
            > {{ $t("message.does-not-account-for-discounts") }} </span
          >
        </span>
        <div class="buttons">
          <div
            class="switch-nudge-button lp-btn"
            @click.prevent="switchToAnnualPlan"
          >
            {{ $t("message.switch-to-annual") }}
          </div>
          <div
            v-if="!noChoice"
            class="dismiss-nudge-button lp-btn"
            @click.prevent="annualNudgeDismissed = true"
          >
            {{ $t("message.no-thanks") }}
          </div>
        </div>
      </div>
      <div class="logo-nudge-section">
        <img
          class="annual-nudge-logo"
          src="/static/dashboard/community/images/annual_nudge_logo.png"
        />
      </div>
    </div>
    <div class="annual-nudge" v-if="showAnnualNudge && !displayAnnualNudge && noChoice">
      <div class="description-nudge-section">
        <span v-html="$t('message.switch-to-monthly-message', [plan.monthly_price / 100])"></span>
        <div class="buttons">
          <div
            class="dismiss-nudge-button lp-btn"
            @click.prevent="switchToMonthlyPlan"
          >
            {{ $t("message.switch-to-monthly") }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="less" scoped>
.subscription-selector {
  .plan-summary {
    background: @primary;
    border: 1px solid @primary;
    border-radius: 3px;
    box-shadow: 1px 1px 3px rgba(0, 0, 0, 0.1);
    padding: 12px 15px 12px 15px;
    position: relative;
    min-height: 67px;
    &:before {
      content: "";
      display: none;
      height: 35px;
      width: 35px;
      border-radius: 999px;
      background: @primary;
      position: absolute;
      left: 15px;
    }
    color: #fff;

    &.enabled {
      cursor: pointer;
      &:hover {
        background: darken(@primary, 3%);
      }
    }

    .plan-name {
      display: block;
      font-weight: bold;

      .min-users {
        font-weight: normal;
      }
    }

    .plan-description {
      display: block;
      color: rgba(255, 255, 255, 0.8);
    }

    .inline-features {
      margin: 1em 0;
      font-size: 0.9em;
      border-left: 2px solid white;
      padding-left: 0.5em;

      p {
        margin: 0.5em 0 0 0;
        color: white;
        padding: 0;
      }
    }
  }
  .proviso,.description-nudge-section {
    /deep/ super {
      font-size: 0.6em;
      top: -0.7em;
      position: relative;
    }
    /deep/ strong {
      font-weight: bold;
    }
  }
  .annual-nudge {
    position: relative;
    background: rgba(0, 0, 0, 0);
    background-color: rgba(0, 0, 0, 0);
    margin-top: 0.3em;
    z-index: 2;
    border-radius: 3px;
    box-shadow: 1px 1px 3px rgba(0, 0, 0, 0.1);
    padding: 20px 15px 15px 15px;
    width: 100%;
    display: flex;
    &:before {
      content: " ";
      left: 0;
      top: 0;
      position: absolute;
      display: block;
      height: 100%;
      width: 100%;
      background: lighten(@primary, 50%);
      // opacity: 0.1;
      z-index: -1;
      border-radius: 3px;
    }
    .description-nudge-section {
      flex-grow: 1;
      .reduction-text-bold {
        font-weight: bold;
      }
      .buttons {
        margin-top: 15px;
        .switch-nudge-button {
          background: @primary;
          color: white;
        }
        .dismiss-nudge-button {
          color: black;
          border: 1px solid black;
        }
      }
    }
    .logo-nudge-section {
      height: 80px;
      padding-left: 20px;
      img {
        height: 100%;
      }
    }
  }
}
</style>

<script>
  import { mapState, mapGetters } from 'vuex';

  export default {
    props: {
      showAnnualNudge: {
        required: false,
        default: true
      }
    },
    data () {
      return {
        annualNudgeDismissed: false,
      }
    },
    computed: {  
    ...mapState({
      plan: state => state.payment.plan,
      licenceCount: state => state.payment.licenceCount,
      learners: state => state.payment.learners,
      pricingPeriod: state => state.payment.pricingPeriod,
      showPlans: state => state.payment.showPlans
    }),
    ...mapGetters(["proviso", "noChoice", "discountActive", "unitPrice"]),
      annualReduction() {
        return (this.plan.monthly_price * 12 - this.plan.annual_price) / 100;
      },
      displayAnnualNudge() {
        return (
          this.showAnnualNudge &&
          this.plan.annual_price &&
          this.plan.monthly_price &&
          this.annualReduction > 0 &&
          !this.annualNudgeDismissed &&
          this.pricingPeriod !== "annual"
        );
      },
    },
    methods: {
      planSelectorClicked() {
        this.$store.commit('setShowPlans', !!(
          this.plan &&
          !this.plan["hidden_from_selection"] &&
          !this.noChoice
        ));
      },
      switchToAnnualPlan() {
        this.$store.commit('setPlan', this.plan);
        this.$store.commit('setPricingPeriod', 'annual');
      },
      switchToMonthlyPlan() {
        this.$store.commit('setPlan', this.plan);
        this.$store.commit('setPricingPeriod', 'monthly');
      }
    }
  }
</script>
